import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { pw9337Api, QueryRequest, QueryResponse, BonusCodeOption } from '../../../services/pw9337Api';

export interface PW9337QueryState {
  loading: boolean;
  result: QueryResponse | null;
  queried: boolean;
  noData: boolean;
  bonusCodes: BonusCodeOption[];
  bonusCodesLoading: boolean;
}

export interface PW9337QueryActions {
  runQuery: (req: QueryRequest) => Promise<void>;
  reset: () => void;
}

export function usePW9337Query(): PW9337QueryState & PW9337QueryActions {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<QueryResponse | null>(null);
  const [queried, setQueried] = useState(false);
  const [noData, setNoData] = useState(false);
  const [bonusCodes, setBonusCodes] = useState<BonusCodeOption[]>([]);
  const [bonusCodesLoading, setBonusCodesLoading] = useState(false);

  // Load bonus codes on mount
  useEffect(() => {
    let mounted = true;
    setBonusCodesLoading(true);
    pw9337Api.listBonusCodes()
      .then((codes) => {
        if (mounted) setBonusCodes(codes);
      })
      .catch(() => {
        if (mounted) message.error('無法載入獎金代號清單');
      })
      .finally(() => {
        if (mounted) setBonusCodesLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const runQuery = useCallback(async (req: QueryRequest) => {
    setLoading(true);
    setQueried(false);
    setNoData(false);
    try {
      const resp = await pw9337Api.query(req);
      if (resp.details && resp.details.length > 0) {
        setResult(resp);
        setNoData(false);
      } else {
        setResult(null);
        setNoData(true);
        message.info('查無符合條件的獎金資料');
      }
      setQueried(true);
    } catch {
      message.error('查詢失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setQueried(false);
    setNoData(false);
  }, []);

  return { loading, result, queried, noData, bonusCodes, bonusCodesLoading, runQuery, reset };
}
